import React, { useCallback, useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import AdminMenu from '../components/AdminMenu';

/**
 * Admin → API Keys & Credentials.
 *
 * One place to see every external credential Lyric depends on (JIRA, GitHub,
 * Gemini, Confluence, SMTP, Okta): whether it's currently valid, WHEN it
 * expires (where the provider exposes that), and a direct link to the console
 * where it can be rotated.
 *
 * Backed by GET /api/admin/credentials (server/routes/adminCredentials.js).
 * Secret values are never sent to the client — only a masked fingerprint.
 */

// Status → badge presentation. Kept in one place so the summary counts and the
// per-row badges stay consistent.
const STATUS_META = {
    valid: { label: 'Valid', bg: '#1a4a1a', fg: '#c8e6c9', border: '#4caf50', icon: '✅', problem: false },
    expiring: { label: 'Expiring soon', bg: '#4a3a12', fg: '#ffe0a3', border: '#f0a020', icon: '⏳', problem: true },
    expired: { label: 'Expired', bg: '#4a1a1a', fg: '#ffcdd2', border: '#ef5350', icon: '⛔', problem: true },
    invalid: { label: 'Invalid', bg: '#4a1a1a', fg: '#ffcdd2', border: '#ef5350', icon: '❌', problem: true },
    error: { label: 'Check failed', bg: '#3a2a12', fg: '#ffe0a3', border: '#c98a20', icon: '⚠️', problem: true },
    unknown: { label: 'Not verifiable', bg: '#26303a', fg: '#bcd', border: '#3a5a72', icon: '❔', problem: false },
    info: { label: 'Info', bg: '#26303a', fg: '#bcd', border: '#3a5a72', icon: 'ℹ️', problem: false },
    not_configured: { label: 'Not configured', bg: '#26262e', fg: '#9aa', border: '#3a3a44', icon: '➖', problem: false },
};

const metaFor = (status) => STATUS_META[status] || STATUS_META.unknown;

function formatExpiry(cred) {
    if (!cred.expiresAt) return null;
    const when = new Date(cred.expiresAt);
    const dateStr = when.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    const d = cred.daysUntilExpiry;
    if (d == null) return dateStr;
    if (d < 0) return `${dateStr} — expired ${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} ago`;
    if (d === 0) return `${dateStr} — expires today`;
    return `${dateStr} — in ${d} day${d === 1 ? '' : 's'}`;
}

export default function AdminCredentials() {
    const { oktaAuth } = useOktaAuth() || {};
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [errorDetail, setErrorDetail] = useState(null);
    const [creds, setCreds] = useState(null);
    const [meta, setMeta] = useState(null);
    const [editing, setEditing] = useState(null);
    const [editDate, setEditDate] = useState('');
    const [editNote, setEditNote] = useState('');
    const [savingId, setSavingId] = useState(null);
    const [rowError, setRowError] = useState({});

    const authHeader = useCallback(async () => {
        let token = '';
        try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
        return token ? { Authorization: `Bearer ${token}` } : {};
    }, [oktaAuth]);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        setErrorDetail(null);
        try {
            const res = await fetch('/api/admin/credentials', { headers: await authHeader() });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(body.error || `Request failed (HTTP ${res.status})`);
                setErrorDetail(body.hint || body.reason || null);
                setCreds(null);
                return;
            }
            setCreds(body.credentials || []);
            setMeta(body.meta || null);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [authHeader]);

    useEffect(() => { load(); }, [load]);

    const cancelEdit = useCallback(() => { setEditing(null); setEditDate(''); setEditNote(''); }, []);

    const startEdit = useCallback((c) => {
        setEditing(c.id);
        setEditDate(c.expiresAt ? String(c.expiresAt).slice(0, 10) : '');
        setEditNote(c.manualExpiryNote || '');
        setRowError((e) => ({ ...e, [c.id]: null }));
    }, []);

    const saveExpiry = useCallback(async (id) => {
        if (!editDate) { setRowError((e) => ({ ...e, [id]: 'Pick a date first.' })); return; }
        setSavingId(id);
        setRowError((e) => ({ ...e, [id]: null }));
        try {
            const res = await fetch(`/api/admin/credentials/${encodeURIComponent(id)}/expiry`, {
                method: 'PUT',
                headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
                body: JSON.stringify({ expiresAt: editDate, note: editNote }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) { setRowError((e) => ({ ...e, [id]: body.error || `Failed (HTTP ${res.status})` })); return; }
            cancelEdit();
            await load();
        } catch (e) {
            setRowError((er) => ({ ...er, [id]: e.message }));
        } finally {
            setSavingId(null);
        }
    }, [authHeader, editDate, editNote, load, cancelEdit]);

    const clearExpiry = useCallback(async (id) => {
        setSavingId(id);
        setRowError((e) => ({ ...e, [id]: null }));
        try {
            const res = await fetch(`/api/admin/credentials/${encodeURIComponent(id)}/expiry`, {
                method: 'DELETE',
                headers: await authHeader(),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) { setRowError((e) => ({ ...e, [id]: body.error || `Failed (HTTP ${res.status})` })); return; }
            cancelEdit();
            await load();
        } catch (e) {
            setRowError((er) => ({ ...er, [id]: e.message }));
        } finally {
            setSavingId(null);
        }
    }, [authHeader, load, cancelEdit]);

    const expiryColor = (c) => (c.status === 'expired'
        ? { color: '#ef9a9a' }
        : c.status === 'expiring' ? { color: '#ffcc80' } : undefined);

    function renderExpiry(c) {
        const busy = savingId === c.id;
        const err = rowError[c.id];

        if (editing === c.id) {
            return (
                <div style={styles.row}>
                    <dt style={styles.dt}>Expires</dt>
                    <dd style={{ ...styles.dd, flex: 1 }}>
                        <div style={styles.expiryEditRow}>
                            <input
                                type="date"
                                value={editDate}
                                onChange={(e) => setEditDate(e.target.value)}
                                style={styles.dateInput}
                                aria-label={`Expiry date for ${c.name}`}
                            />
                            <button type="button" onClick={() => saveExpiry(c.id)} disabled={busy} style={styles.saveBtn}>
                                {busy ? 'Saving…' : 'Save'}
                            </button>
                            <button type="button" onClick={cancelEdit} disabled={busy} style={styles.cancelBtn}>Cancel</button>
                        </div>
                        <input
                            type="text"
                            value={editNote}
                            onChange={(e) => setEditNote(e.target.value)}
                            placeholder="Optional note (e.g. rotate with IT ticket)"
                            maxLength={500}
                            style={styles.noteInput}
                            aria-label={`Note for ${c.name} expiry`}
                        />
                        {err && <div style={styles.rowErr}>{err}</div>}
                    </dd>
                </div>
            );
        }

        if (c.expiresAt && !c.manualExpiry) {
            return <Row label="Expires" value={formatExpiry(c) || 'No expiration set'} valueStyle={expiryColor(c)} />;
        }

        if (c.manualExpiry) {
            return (
                <>
                    <Row label="Expires" value={`${formatExpiry(c)} · admin-set`} valueStyle={expiryColor(c)} />
                    {c.manualExpiryNote && <Row label="Note" value={c.manualExpiryNote} muted />}
                    {(c.expiryUpdatedBy || c.expiryUpdatedAt) && (
                        <Row
                            label="Set by"
                            value={`${c.expiryUpdatedBy || 'admin'}${c.expiryUpdatedAt ? ` · ${new Date(c.expiryUpdatedAt).toLocaleDateString()}` : ''}`}
                            muted
                        />
                    )}
                    <div style={styles.expiryActions}>
                        <button type="button" onClick={() => startEdit(c)} style={styles.linkBtn}>Edit</button>
                        <button type="button" onClick={() => clearExpiry(c.id)} disabled={busy} style={styles.linkBtnDanger}>
                            {busy ? '…' : 'Clear'}
                        </button>
                        {err && <span style={styles.rowErr}>{err}</span>}
                    </div>
                </>
            );
        }

        if (c.manualExpiryAllowed) {
            return (
                <>
                    <Row label="Expires" value="Not exposed by provider" muted />
                    <div style={styles.expiryActions}>
                        <button type="button" onClick={() => startEdit(c)} style={styles.linkBtn}>+ Add expiry date</button>
                        {err && <span style={styles.rowErr}>{err}</span>}
                    </div>
                </>
            );
        }

        if (c.configured) return <Row label="Expires" value="Not applicable" muted />;
        return null;
    }

    const problems = (creds || []).filter((c) => metaFor(c.status).problem).length;
    const configured = (creds || []).filter((c) => c.configured).length;

    return (
        <div style={styles.page}>
            <div style={styles.pageHeader}>
                <h2 style={styles.title}>🔑 API Keys &amp; Credentials</h2>
                <AdminMenu />
            </div>
            <p style={styles.subtitle}>
                Every external credential <strong>Lyric</strong> uses, whether it&apos;s currently
                valid, when it expires (where the provider exposes that), and a link to rotate it.
                Checks are <strong>read-only</strong> and secret values are never shown — only a
                masked fingerprint.
            </p>

            <div style={styles.actions}>
                <button type="button" onClick={load} disabled={loading} style={loading ? styles.btnPrimaryDisabled : styles.btnPrimary}>
                    {loading ? 'Checking…' : '↻ Re-check all'}
                </button>
                {creds && (
                    <span style={styles.summary}>
                        {configured} configured ·{' '}
                        <span style={{ color: problems ? '#ffb74d' : '#81c784', fontWeight: 700 }}>
                            {problems ? `${problems} need attention` : 'all healthy'}
                        </span>
                        {meta?.checkedAt && <span style={styles.checkedAt}> · checked {new Date(meta.checkedAt).toLocaleTimeString()}</span>}
                    </span>
                )}
            </div>

            {error && (
                <div style={styles.error}>
                    <strong>{error}</strong>
                    {errorDetail && <div style={styles.errorHint}>{errorDetail}</div>}
                </div>
            )}

            {loading && !creds && <p style={styles.help}>Probing providers…</p>}

            <div style={styles.grid}>
                {(creds || []).map((c) => {
                    const sm = metaFor(c.status);
                    return (
                        <section key={c.id} style={{ ...styles.card, borderLeft: `4px solid ${sm.border}` }}>
                            <div style={styles.cardHead}>
                                <div>
                                    <div style={styles.cardName}>{c.name}</div>
                                    <div style={styles.cardProvider}>{c.provider}</div>
                                </div>
                                <span style={{ ...styles.badge, background: sm.bg, color: sm.fg, borderColor: sm.border }}>
                                    {sm.icon} {sm.label}
                                </span>
                            </div>

                            {c.detail && <p style={styles.detail}>{c.detail}</p>}

                            <dl style={styles.meta}>
                                {c.category && <Row label="Category" value={c.category} />}
                                {c.account && <Row label="Account" value={c.account} mono />}
                                {renderExpiry(c)}
                                {Array.isArray(c.scopes) && c.scopes.length > 0 && (
                                    <Row label="Scopes" value={c.scopes.join(', ')} mono />
                                )}
                                {c.fingerprint && <Row label="Value" value={c.fingerprint} mono />}
                                {c.envVars && c.envVars.length > 0 && (
                                    <Row label="Env" value={c.envVars.join(', ')} mono muted />
                                )}
                            </dl>

                            {c.note && <p style={styles.note}>💡 {c.note}</p>}

                            <div style={styles.cardFoot}>
                                <a href={c.manageUrl} target="_blank" rel="noopener noreferrer" style={styles.manageLink}>
                                    Manage / rotate ↗
                                </a>
                                {c.manageLabel && <span style={styles.manageHint}>{c.manageLabel}</span>}
                            </div>
                        </section>
                    );
                })}
            </div>

            {meta?.podIdentity && (
                <p style={styles.pod}>
                    Checked from pod <code>{meta.podIdentity.hostname}</code>
                    {meta.podIdentity.region ? ` (${meta.podIdentity.region})` : ''}.
                    Credentials live in the <code>tools/zyra-ai/config-base</code> AWS Secrets Manager secret;
                    after rotating a key upstream, update it there and restart the pod.
                </p>
            )}
        </div>
    );
}

function Row({ label, value, mono, muted, valueStyle }) {
    return (
        <div style={styles.row}>
            <dt style={styles.dt}>{label}</dt>
            <dd style={{ ...styles.dd, ...(mono ? styles.mono : {}), ...(muted ? { color: '#7a828c' } : {}), ...(valueStyle || {}) }}>{value}</dd>
        </div>
    );
}

const styles = {
    page: { padding: '24px', maxWidth: '1100px', margin: '0 auto', color: '#fff' },
    pageHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' },
    title: { margin: 0, color: '#4fc3f7' },
    subtitle: { color: '#aaa', marginTop: 0, lineHeight: 1.5 },
    actions: { display: 'flex', alignItems: 'center', gap: '16px', margin: '16px 0', flexWrap: 'wrap' },
    summary: { color: '#bbb', fontSize: '13px' },
    checkedAt: { color: '#777' },
    help: { color: '#888', fontSize: '0.9rem' },
    btnPrimary: { padding: '8px 14px', background: '#0052cc', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 },
    btnPrimaryDisabled: { padding: '8px 14px', background: '#2f3a44', color: '#889', border: 'none', borderRadius: '4px', cursor: 'not-allowed', fontWeight: 600 },
    error: { padding: '10px 12px', background: '#4a1a1a', border: '1px solid #ef5350', borderRadius: '4px', color: '#ffcdd2', margin: '8px 0' },
    errorHint: { color: '#ffab91', fontSize: '12px', marginTop: '4px' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '14px', marginTop: '8px' },
    card: { padding: '16px', borderRadius: '8px', background: '#1f2429', border: '1px solid #2f3a44' },
    cardHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' },
    cardName: { fontSize: '16px', fontWeight: 700, color: '#e8f4ff' },
    cardProvider: { fontSize: '12px', color: '#8a939d', marginTop: '2px' },
    badge: { display: 'inline-block', padding: '3px 9px', border: '1px solid', borderRadius: '12px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' },
    detail: { color: '#cbd3da', fontSize: '13px', lineHeight: 1.5, margin: '10px 0 8px' },
    meta: { margin: '8px 0 0', display: 'flex', flexDirection: 'column', gap: '4px' },
    row: { display: 'flex', gap: '8px', fontSize: '12.5px', lineHeight: 1.4 },
    dt: { color: '#7f8a94', minWidth: '74px', flexShrink: 0 },
    dd: { margin: 0, color: '#d6dde3', wordBreak: 'break-word' },
    mono: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '12px' },
    note: { color: '#9fb0bd', fontSize: '12px', lineHeight: 1.5, margin: '10px 0 0', background: '#161c21', border: '1px dashed #2f3a44', borderRadius: '6px', padding: '7px 9px' },
    cardFoot: { display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px', flexWrap: 'wrap' },
    manageLink: { color: '#4fc3f7', textDecoration: 'none', fontSize: '13px', fontWeight: 600, border: '1px solid #2a4a72', background: '#1e3a5f', padding: '5px 10px', borderRadius: '4px' },
    manageHint: { color: '#7f8a94', fontSize: '11px' },
    expiryEditRow: { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
    dateInput: { background: '#0f1418', color: '#e8f4ff', border: '1px solid #2f3a44', borderRadius: '4px', padding: '4px 6px', fontSize: '12.5px', colorScheme: 'dark' },
    noteInput: { marginTop: '6px', width: '100%', boxSizing: 'border-box', background: '#0f1418', color: '#e8f4ff', border: '1px solid #2f3a44', borderRadius: '4px', padding: '5px 7px', fontSize: '12px' },
    saveBtn: { padding: '4px 10px', background: '#0052cc', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 },
    cancelBtn: { padding: '4px 10px', background: 'transparent', color: '#9fb0bd', border: '1px solid #2f3a44', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' },
    expiryActions: { display: 'flex', gap: '10px', alignItems: 'center', marginTop: '2px' },
    linkBtn: { background: 'none', border: 'none', color: '#4fc3f7', cursor: 'pointer', fontSize: '12px', padding: 0, fontWeight: 600 },
    linkBtnDanger: { background: 'none', border: 'none', color: '#ef9a9a', cursor: 'pointer', fontSize: '12px', padding: 0, fontWeight: 600 },
    rowErr: { color: '#ef9a9a', fontSize: '11.5px', marginTop: '4px' },
    pod: { color: '#7f8a94', fontSize: '12px', marginTop: '20px', lineHeight: 1.6 },
};
